import { Observable, observe } from "../reactive";
import * as leaflet from "leaflet";
import { FloorLevels, ZoomLevels } from "./ZoomLevels";
var observe_combined = Observable.observe_combined;
export class MapEntity extends leaflet.FeatureGroup {
    constructor() {
        super([]);
        this.tooltip_hook = observe(null);
        this.interactive = false;
        this.tooltip = null;
        this.parent = null;
        this.zoom_sensitivity_layers = ZoomLevels.none;
        this.floor_sensitivity_layers = FloorLevels.none;
        this.render_group = observe({ floor: 0, zoom: 0 }).equality((a, b) => a.floor == b.floor && a.zoom == b.zoom);
        this.highlighted = observe(false);
        this.visible = observe(true);
        this.opacity = observe(1);
        this.culled = observe(false);
        this.rendering_requested = false;
        this.rendered_props = null;
        this.desired_render_props = observe(null).equality(MapEntity.RenderProps.equals);
        this.on("mouseover", () => {
            if (this.interactive)
                this.parent?.updateHovering(this, true);
        });
        this.on("mouseout", () => {
            if (this.interactive)
                this.parent?.updateHovering(this, false);
        });
        observe_combined({
            v: this.visible,
            c: this.culled,
            o: this.opacity,
            h: this.highlighted,
            g: this.render_group
        }).subscribe(() => this.desired_render_props.set(this.getDesiredRenderProps()), true);
        this.desired_render_props.subscribe(() => this.requestRendering());
    }
    setInteractive(value = true) {
        this.interactive = value;
        if (!value)
            this.parent?.updateHovering(this, false);
        return this;
    }
    setTooltip(f) {
        this.tooltip = f;
        if (f)
            this.setInteractive(true);
        return this;
    }
    async renderTool() {
        return this.tooltip?.(this);
    }
    remove() {
        if (this.parent) {
            this.parent.removeEntity(this);
        }
        else {
            super.remove();
        }
        return this;
    }
    getDesiredRenderProps() {
        const floor_group = this.floor_sensitivity_layers.get(this.render_group.value().floor);
        const zoom_group = this.zoom_sensitivity_layers.get(this.render_group.value().zoom);
        return {
            render_at_all: !floor_group.hidden_here && !zoom_group.hidden_here && this.visible.value() && !this.culled.value(),
            opacity: this.opacity.value(),
            highlight: this.highlighted.value(),
            zoom_group_index: this.render_group.value().zoom,
            floor_group_index: this.render_group.value().floor,
        };
    }
    isStillHovered() {
        return this.getLayers().some(e => {
            if (e instanceof leaflet.Marker || e instanceof leaflet.Path) {
                return e.getElement()?.matches(":hover");
            }
            return false;
        });
    }
    isActive() {
        return this.parent?.activeEntity() == this;
    }
    setVisible(v) {
        this.visible.set(v);
        return this;
    }
    requestRendering(force = false) {
        if (this.parent && (!this.rendering_requested || force)) {
            this.rendering_requested = true;
            this.parent.rendering.request(this);
        }
    }
    render(force = false) {
        const props = this.desired_render_props.value();
        if (!this.parent?.getMap())
            return;
        if (!this.rendering_requested && !force && MapEntity.RenderProps.equals(this.rendered_props, props))
            return;
        this.rendering_requested = false;
        this.rendered_props = props;
        this.clearLayers();
        if (!props.render_at_all)
            return;
        this.tooltip_hook.set(this.render_implementation(props));
    }
    requestActivation(force_interactive) {
        return this.parent?.requestEntityActivation(this, force_interactive);
    }
    async resetActivation() {
        if (this.isActive()) {
            await this.parent?.requestEntityActivation(null);
        }
    }
    async contextMenu(event) {
        return null;
    }
    setCulled(v) {
        this.culled.set(v);
    }
    setOpacity(v) {
        this.opacity.set(v);
    }
    setFloorAndZoom(floor, zoom) {
        const z = this.zoom_sensitivity_layers.getGroupIndex(zoom);
        const f = this.floor_sensitivity_layers.getGroupIndex(floor);
        this.render_group.set({ floor: f, zoom: z });
    }
}
(function (MapEntity) {
    MapEntity.default_zoom_scale_layers = new ZoomLevels([
        { min: -100, value: { scale: 0.25 } },
        { min: 0, value: { scale: 0.5 } },
        { min: 1.5, value: { scale: 1 } },
    ]);
    MapEntity.default_local_zoom_scale_layers = new ZoomLevels([
        { min: -100, hidden_here: true, value: { scale: 0.25 } },
        { min: 0, value: { scale: 0.5 } },
        { min: 5, value: { scale: 1 } },
    ]);
    let RenderProps;
    (function (RenderProps) {
        function equals(a, b) {
            if (a == null || b == null)
                return false;
            if (!a.render_at_all && !b.render_at_all)
                return true;
            return a.render_at_all == b.render_at_all &&
                a.highlight == b.highlight &&
                a.opacity == b.opacity &&
                a.floor_group_index == b.floor_group_index &&
                a.zoom_group_index == b.zoom_group_index;
        }
        RenderProps.equals = equals;
    })(RenderProps = MapEntity.RenderProps || (MapEntity.RenderProps = {}));
})(MapEntity || (MapEntity = {}));
